import { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Story } from '@/hooks/useStories';
import { formatDistanceToNow } from 'date-fns';
import { hy } from 'date-fns/locale';

interface StoryViewerProps {
  stories: Story[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

export const StoryViewer = ({ 
  stories, 
  currentIndex, 
  onClose, 
  onNext, 
  onPrev 
}: StoryViewerProps) => {
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentStory = stories[currentIndex];
  const duration = currentStory.display_duration || 5000; // Use story's display duration or default to 5 seconds

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (currentIndex < stories.length - 1) {
            onNext();
          } else {
            onClose();
          }
          return 0;
        }
        return prev + (100 / duration) * 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentIndex, stories.length, onNext, onClose, isPaused]);

  useEffect(() => {
    setProgress(0);
  }, [currentIndex]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) onPrev();
    if (e.key === 'ArrowRight' && currentIndex < stories.length - 1) onNext();
    if (e.key === ' ') {
      e.preventDefault();
      setIsPaused(!isPaused);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, stories.length, onNext, onPrev, onClose, isPaused]);

  const userName = currentStory.profiles 
    ? `${currentStory.profiles.first_name} ${currentStory.profiles.last_name}`
    : 'Անանուն';

  return (
    <div 
      className="fixed inset-0 bg-black z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Progress bars */}
      <div className="absolute top-4 left-4 right-4 flex gap-1 z-10">
        {stories.map((_, index) => (
          <div
            key={index}
            className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
          >
            <div
              className="h-full bg-white transition-all duration-100"
              style={{
                width: index < currentIndex 
                  ? '100%' 
                  : index === currentIndex 
                    ? `${progress}%` 
                    : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-8 left-4 right-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3 text-white">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-yellow-500 p-0.5">
            <div className="w-full h-full rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-sm font-medium">
                {userName.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
          </div>
          <div>
            <p className="font-medium">{userName}</p>
            <p className="text-sm text-white/80">
              {formatDistanceToNow(new Date(currentStory.created_at), { 
                addSuffix: true,
                locale: hy 
              })}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-white/80">
            <Eye size={16} />
            <span className="text-sm">{currentStory.view_count}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X size={20} />
          </Button>
        </div>
      </div>

      {/* Navigation */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10"
        >
          <ChevronLeft size={32} />
        </button>
      )}

      {currentIndex < stories.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10"
        >
          <ChevronRight size={32} />
        </button>
      )}

      {/* Story content */}
      <div
        className="relative max-w-md max-h-[80vh] w-full mx-4"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {currentStory.media_type === 'photo' ? (
          <img
            src={currentStory.media_url}
            alt={currentStory.alt_text || currentStory.title || 'Story'}
            className="w-full h-full object-contain rounded-lg"
          />
        ) : (
          <video
            ref={videoRef}
            src={currentStory.media_url}
            className="w-full h-full object-contain rounded-lg"
            autoPlay
            muted
            controls={false}
            onLoadedMetadata={() => {
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
              }
            }}
          />
        )}
        
        {/* Action button for link */}
        <div className="absolute bottom-4 left-4 right-4 text-white">
          
          {/* Action button for link */}
          {currentStory.link_url && currentStory.button_text && (
            <div className="flex justify-center">
              <a
                href={currentStory.link_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium rounded-full hover:bg-gray-100 transition-colors duration-200 shadow-lg"
              >
                {currentStory.button_text}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};